import {
  DEFAULT_INPUT_ID,
  useAudioDevicesStore,
} from '@/stores/audioDevicesStore'
import {
  useSequencerStore,
  selectCurrentContainer,
} from '@/stores/sequencerStore'
import { resolveBusInput } from '@/audio/busRegistry'
import {
  hasWebGetUserMedia,
  monitorUnavailableReason,
} from '@/audio/capabilities'
import { hasNativeAudioPlugin } from '@/lib/nativeAudio'
import {
  ensureNativeInputStream,
  NativeInputStream,
} from '@/audio/NativeInputStream'
import { ensureMeter, disposeMeter, MONITOR_METER_ID } from '@/audio/meters'

/**
 * Live audio-input pipeline. Owns the `MediaStream` + its
 * `MediaStreamAudioSourceNode`; subscribes to `audioDevicesStore`
 * for device / bus / monitor changes and rewires accordingly.
 *
 * Permission is only requested when monitoring turns ON — the store
 * field is off by default, so a reload doesn't silently re-prompt
 * the user. Errors (permission denied, device unplugged) surface via
 * `onError`; the UI renders them as a soft banner.
 *
 * This module also exposes `getCurrentStream()` so Phase 3's
 * `MediaRecorder`-based audio recording can tap the same stream
 * without re-requesting permission.
 */

interface AudioInputListener {
  onStatus?: (status: 'idle' | 'starting' | 'active' | 'error') => void
  onError?: (message: string) => void
}

export class AudioInput {
  private ctx: AudioContext
  private sourceNode: MediaStreamAudioSourceNode | null = null
  private stream: MediaStream | null = null
  private connectedTo: AudioNode | null = null
  private currentDeviceId: string | null = null
  private listeners = new Set<AudioInputListener>()
  private status: 'idle' | 'starting' | 'active' | 'error' = 'idle'
  /** Native bridge — owned only when `hasNativeAudioPlugin()`. When
   *  set, start/stop/connectTo delegate here and the getUserMedia
   *  fields (`sourceNode`, `stream`) are unused. */
  private native: NativeInputStream | null = null
  private nativeUnsubscribe: (() => void) | null = null

  constructor(ctx: AudioContext) {
    this.ctx = ctx
    // Legacy monitor always follows the OS default input — user
    // picks a specific device via the Source dropdown, and when
    // that changes we swap out the backing NativeInputStream for
    // the new device's keyed instance (see `start()`).
    if (hasNativeAudioPlugin()) {
      this.bindNative(ensureNativeInputStream(ctx, ''))
    }
  }

  /** Swap the active native-input-stream reference + re-hook the
   *  listener subscription. Called from `start(deviceId)` when the
   *  user picks a different input; the old instance keeps its
   *  lifecycle (other consumers may still be using it) and we
   *  detach cleanly. */
  private bindNative(next: NativeInputStream): void {
    if (this.native === next) return
    if (this.nativeUnsubscribe) {
      this.nativeUnsubscribe()
      this.nativeUnsubscribe = null
    }
    this.native = next
    // Forward native status/error into our listener surface so the
    // AudioPanel banner works identically on both runtimes.
    this.nativeUnsubscribe = next.subscribe({
      onStatus: status => this.setStatus(status),
      onError: message => {
        for (const l of this.listeners) l.onError?.(message)
      },
    })
  }

  /** Subscribe to status transitions + error messages. Returns an
   *  unsubscribe function. UI components attach this to render the
   *  current state and react to failures. */
  subscribe(listener: AudioInputListener): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  /**
   * Start the capture pipeline for the given device. Safe to call
   * when already running — re-uses the existing stream if the
   * deviceId hasn't changed, otherwise tears down + restarts.
   * Rejects on permission denial; callers should set monitor back
   * to OFF when this rejects so the UI surfaces the failure.
   */
  async start(deviceId: string): Promise<void> {
    if (this.status === 'active' && this.currentDeviceId === deviceId) return
    if (this.status === 'active') this.stop()
    // Native bridge (Catalyst / iPad): delegate to the per-device
    // NativeInputStream. Status + error events forward through the
    // subscription wired in `bindNative`. If the user picked a
    // different device than the legacy monitor was last bound to,
    // swap instances so the filter-by-deviceId in NativeInputStream
    // gets the right frames.
    if (hasNativeAudioPlugin()) {
      const next = ensureNativeInputStream(this.ctx, deviceId)
      this.bindNative(next)
      await next.start()
      this.currentDeviceId = deviceId
      // Tap the worklet's output so the meter reads the post-bridge
      // signal. The AnalyserNode is passthrough, so the connection
      // doesn't interfere with MultiInput's splitter also consuming
      // the node.
      const outputNode = next.getOutputNode()
      if (outputNode) {
        const analyser = ensureMeter(this.ctx, MONITOR_METER_ID)
        outputNode.connect(analyser)
      }
      if (this.connectedTo) next.connectTo(this.connectedTo)
      return
    }
    // Guard before touching `navigator.mediaDevices`. On Mac Catalyst
    // the property is undefined and the raw TypeError leaks through
    // to the UI as a useless "undefined is not an object" — surface
    // a real reason instead so the user understands native monitoring
    // is pending rather than wondering if they broke something.
    if (!hasWebGetUserMedia()) {
      const reason =
        monitorUnavailableReason() ?? 'Input monitoring is not available'
      this.setStatus('error')
      for (const l of this.listeners) l.onError?.(reason)
      throw new Error(reason)
    }
    this.setStatus('starting')
    try {
      // Explicit constraints turn off the browser's default voice
      // processing — we want raw instrument audio, not phone-call
      // tuning. `deviceId: { exact }` when the user picked a
      // specific device; `{ ideal: default }` for the sentinel.
      const audio: MediaTrackConstraints =
        deviceId && deviceId !== DEFAULT_INPUT_ID
          ? { deviceId: { exact: deviceId } }
          : {}
      audio.echoCancellation = false
      audio.noiseSuppression = false
      audio.autoGainControl = false
      const stream = await navigator.mediaDevices.getUserMedia({ audio })
      this.stream = stream
      this.currentDeviceId = deviceId
      this.sourceNode = this.ctx.createMediaStreamSource(stream)
      // Tap the source with a meter so the AudioPanel's legacy
      // Monitor row can render a peak bar. Analyser is passthrough,
      // so this doesn't affect the signal routed to the bus.
      const analyser = ensureMeter(this.ctx, MONITOR_METER_ID)
      this.sourceNode.connect(analyser)
      // Reconnect to the previously-chosen destination, if any. The
      // manager wires this up via connectTo() right after start().
      if (this.connectedTo && this.sourceNode) {
        this.sourceNode.connect(this.connectedTo)
      }
      this.setStatus('active')
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Audio input permission denied'
      this.setStatus('error')
      for (const l of this.listeners) l.onError?.(message)
      throw err
    }
  }

  /**
   * Route the input's source node to a destination. `null` to
   * disconnect (silent monitoring). Safe to call at any time —
   * before `start` it only updates the pending target; after start
   * it immediately connects.
   */
  connectTo(destination: AudioNode | null): void {
    if (this.connectedTo === destination) return
    if (this.native) {
      this.connectedTo = destination
      this.native.connectTo(destination)
      return
    }
    if (this.sourceNode && this.connectedTo) {
      try {
        this.sourceNode.disconnect(this.connectedTo)
      } catch {
        /* already disconnected */
      }
    }
    this.connectedTo = destination
    if (this.sourceNode && destination) {
      this.sourceNode.connect(destination)
    }
  }

  /** Tear the pipeline down. Stops all tracks so the browser's
   *  recording indicator clears. Idempotent. */
  stop(): void {
    if (this.native) {
      this.native.stop()
      disposeMeter(MONITOR_METER_ID)
      // NativeInputStream emits its own 'idle' status via the
      // subscription, so we don't setStatus here — avoids a
      // duplicate transition.
      this.currentDeviceId = null
      this.connectedTo = null
      return
    }
    if (this.sourceNode) {
      try {
        this.sourceNode.disconnect()
      } catch {
        /* already disconnected */
      }
      this.sourceNode = null
    }
    if (this.stream) {
      for (const t of this.stream.getTracks()) t.stop()
      this.stream = null
    }
    disposeMeter(MONITOR_METER_ID)
    this.currentDeviceId = null
    this.connectedTo = null
    this.setStatus('idle')
  }

  /** Exposed for Phase 3 MediaRecorder — lets the recorder tap the
   *  same stream without re-requesting permission. */
  getStream(): MediaStream | null {
    return this.stream
  }

  getStatus(): 'idle' | 'starting' | 'active' | 'error' {
    return this.status
  }

  private setStatus(next: typeof this.status): void {
    this.status = next
    for (const l of this.listeners) l.onStatus?.(next)
  }
}

// ── Singleton + store subscription ────────────────────────────────

let sharedInput: AudioInput | null = null

/** Idempotent initializer — useAudioEngine calls this once it has a
 *  live AudioContext (i.e. after the first user gesture resumes it). */
export function ensureAudioInput(ctx: AudioContext): AudioInput {
  if (sharedInput) return sharedInput
  sharedInput = new AudioInput(ctx)
  // Apply the current store state + subscribe for changes. Changes
  // fire syncAudioInput() which reconciles monitor / device / bus.
  syncAudioInput()
  useAudioDevicesStore.subscribe(syncAudioInput)
  // Also re-sync when the sequencer container changes — when the
  // user adds or removes an InputSource on native, AudioInput needs
  // to re-evaluate whether to connect its whole-stream monitor or
  // yield to MultiInput's per-channel routing.
  useSequencerStore.subscribe(syncAudioInput)
  return sharedInput
}

/** Access the singleton without requiring an AudioContext. Returns
 *  null if the engine hasn't warmed up yet. Safe to call from UI
 *  code paths that just want to read getStream() or status. */
export function getAudioInput(): AudioInput | null {
  return sharedInput
}

/**
 * Reconcile the live AudioInput with the store. Called on every
 * store change. Three transitions:
 *   - Monitor turned ON  → start(deviceId), connectTo(bus.input)
 *   - Monitor turned OFF → stop()
 *   - Monitor stays ON, device/bus changed → restart + reconnect
 */
function syncAudioInput(): void {
  if (!sharedInput) return
  const state = useAudioDevicesStore.getState()
  const { inputMonitorEnabled, selectedInputId, inputBusId } = state
  if (!inputMonitorEnabled) {
    if (sharedInput.getStatus() !== 'idle') sharedInput.stop()
    return
  }
  // On native, MultiInputRouter shares the same underlying
  // NativeInputStream. If the user has explicit per-channel
  // sources configured, they take priority: MultiInput owns the
  // routing and AudioInput's whole-stream bus connection would
  // just double the audio. Skip the legacy connectTo in that case
  // while still keeping the stream warmed up (so MultiInput's
  // per-source routing reads off the same tap).
  const sources =
    selectCurrentContainer(useSequencerStore.getState())?.inputSources ?? []
  const yieldToMultiInput = hasNativeAudioPlugin() && sources.length > 0
  // Kick off start even if already active — the method short-
  // circuits when device hasn't changed. Errors bubble to the
  // listeners (bound in AudioPanel) and the store is flipped off
  // via catch(() => setInputMonitorEnabled(false)).
  sharedInput
    .start(selectedInputId)
    .then(() => {
      if (yieldToMultiInput) {
        // Disconnect any prior bus routing so we don't leave a
        // stale connection playing through the legacy path while
        // MultiInput also routes.
        sharedInput?.connectTo(null)
        return
      }
      sharedInput?.connectTo(resolveBusInput(inputBusId) ?? null)
    })
    .catch(() => {
      // Turn the monitor flag off so the UI reflects reality.
      // We do this in the manager rather than the component so
      // any caller triggering a device/bus change handles
      // permission errors the same way.
      useAudioDevicesStore.getState().setInputMonitorEnabled(false)
    })
}
